import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, ExternalLink, Sparkles } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { CONCIERGE_PRESETS } from "@/lib/concierge-presets";

export const Route = createFileRoute("/_authenticated/admin/concierge-links")({
  head: () => ({
    meta: [
      { title: "Admin · Enlaces Concierge para OTAs — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ConciergeLinksPage,
});

function ConciergeLinksPage() {
  const [copied, setCopied] = useState<string | null>(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://www.mostrandomexico.mx";
  const presets = Object.values(CONCIERGE_PRESETS);

  async function copy(url: string, id: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <AdminNav />
        <h1 className="mt-6 flex items-center gap-2 text-2xl font-bold">
          <Sparkles className="h-5 w-5 text-primary" />
          Enlaces Concierge para OTAs
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enlaces públicos (sin login) que abren directamente la IA Concierge de un destino.
          Compártelos por WhatsApp, email o cualquier canal de la OTA.
        </p>

        <div className="mt-6 space-y-4">
          {presets.map((p) => {
            const url = `${origin}/concierge/${p.id}`;
            return (
              <article key={p.id} className="rounded-xl border border-border bg-card p-4">
                <h2 className="font-semibold">
                  {p.title} · <span className="uppercase text-muted-foreground">{p.lang}</span>
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">{p.destination}</p>
                <code className="mt-3 block break-all rounded-lg bg-muted px-3 py-2 text-xs">{url}</code>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => void copy(url, p.id)}>
                    {copied === p.id ? (
                      <Check className="mr-1.5 h-4 w-4" />
                    ) : (
                      <Copy className="mr-1.5 h-4 w-4" />
                    )}
                    {copied === p.id ? "Copiado" : "Copiar enlace para OTA"}
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      Abrir <ExternalLink className="ml-1.5 h-4 w-4" />
                    </a>
                  </Button>
                  <Button size="sm" variant="ghost" asChild>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`${p.title} — ${url}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Compartir por WhatsApp
                    </a>
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
